<script setup lang="ts">
  import type { PropType } from 'vue'

  import { computed, onMounted, ref } from 'vue'
  import { useI18n } from 'vue-i18n'

  import { User01Icon } from '@gohighlevel/ghl-icons/24/outline'

  import CPButton from '@ui/components/CPButton.vue'
  import CPForm from '@ui/components/CPForm.vue'
  import CPFormItem from '@ui/components/CPFormItem.vue'
  import CPInput from '@ui/components/CPInput.vue'
  import CPText from '@ui/components/CPText.vue'

  import { useCurrentUser } from '@common/current-user-layer/composables/useCurrentUser'

  import AuthBrandingHeader from '../AuthBrandingHeader.vue'

  import { createElementId } from '../../../core-layer/utils'
  import { LoginEvents, type ScreenChangeData } from '../../types/auth'
  import { getErrorMessage, SANITIZE_REGEX_PATTERN } from '../../utils'
  import { MigrationUtility } from '../../utils/migration'

  defineProps({
    showBranding: {
      type: Boolean,
      default: false,
    },
    portalLogo: {
      type: String as PropType<string | null>,
      default: null,
    },
    portalName: {
      type: String,
      default: 'ClientPortal',
    },
  })

  const emit = defineEmits<{
    (e: 'changeScreen', value: ScreenChangeData): void
    (e: 'loginCallback', value: { eventType: string }): void
  }>()

  const { t } = useI18n()
  const { loadProfile, updateProfile, store: userStore } = useCurrentUser()

  const formRef = ref()
  const loading = ref(false)
  const loadingUser = ref(true)
  const errorMessage = ref<string | null>(null)

  const formValue = ref({
    fullName: '',
    slug: '',
    title: '',
  })
  const originalSlug = ref('')

  const fieldErrors = ref({
    fullName: '',
    slug: '',
    title: '',
  })

  const SLUG_MIN_LENGTH = 3
  const SLUG_MAX_LENGTH = 50

  const rules = {
    fullName: {
      required: true,
      validator: (_: unknown, value: string) => {
        fieldErrors.value.fullName = ''
        if (!value?.trim()) {
          fieldErrors.value.fullName = t('auth.profileConfirmation.validation.nameRequired')
          return false
        }
        if (SANITIZE_REGEX_PATTERN.test(value)) {
          fieldErrors.value.fullName = t('auth.validation.textUnsafe')
          return false
        }
        return true
      },
      message: t('auth.profileConfirmation.validation.nameRequired'),
      trigger: ['input', 'blur'],
    },
    slug: {
      validator: (_: unknown, value: string) => {
        fieldErrors.value.slug = ''
        if (!value || value.length === 0) return true

        const checks: [boolean, string][] = [
          [value.length < SLUG_MIN_LENGTH, t('auth.profileConfirmation.validation.slugTooShort')],
          [/^\d+$/.test(value), t('auth.profileConfirmation.validation.slugOnlyNumbers')],
          [/^\d/.test(value), t('auth.profileConfirmation.validation.slugStartsWithNumber')],
          [/^-/.test(value), t('auth.profileConfirmation.validation.slugStartsWithHyphen')],
          [/-$/.test(value), t('auth.profileConfirmation.validation.slugEndsWithHyphen')],
          [/--/.test(value), t('auth.profileConfirmation.validation.slugDoubleHyphen')],
          [/\s/.test(value), t('auth.profileConfirmation.validation.slugContainsSpace')],
          [!/^[\p{L}\p{N}][\p{L}\p{N}-]*$/u.test(value), t('auth.profileConfirmation.validation.slugSpecialChars')],
          [value.length > SLUG_MAX_LENGTH, t('auth.profileConfirmation.validation.slugTooLong')],
        ]

        for (const [condition, message] of checks) {
          if (condition) {
            fieldErrors.value.slug = message
            return new Error(message)
          }
        }
        return true
      },
      trigger: ['input', 'blur'],
    },
    title: {
      validator: (_: unknown, value: string) => {
        fieldErrors.value.title = ''
        if (!value?.trim()) return true
        if (SANITIZE_REGEX_PATTERN.test(value)) {
          fieldErrors.value.title = t('auth.validation.textUnsafe')
          return false
        }
        return true
      },
      trigger: ['input', 'blur'],
    },
  }

  const isFormValid = computed(() => {
    return !!formValue.value.fullName?.trim() && !fieldErrors.value.fullName && !fieldErrors.value.slug && !fieldErrors.value.title
  })

  const hydrateFormFromProfile = (
    profile?: {
      fullName?: string
      slug?: string
      title?: string
    } | null
  ) => {
    if (!profile) return false
    formValue.value = {
      fullName: profile.fullName || '',
      slug: profile.slug || '',
      title: profile.title || '',
    }
    originalSlug.value = profile.slug || ''
    return true
  }

  onMounted(async () => {
    try {
      const profile = await loadProfile(true)
      const hydrated = hydrateFormFromProfile(profile)
      if (!hydrated) {
        hydrateFormFromProfile(userStore.profile)
      }
    } catch (err: unknown) {
      errorMessage.value = getErrorMessage(err)
      hydrateFormFromProfile(userStore.profile)
    } finally {
      loadingUser.value = false
    }
  })

  function completeMigration() {
    MigrationUtility.clear()
  }

  async function handleConfirm() {
    if (!formRef.value) return
    try {
      await formRef.value.getForm().validate()
    } catch {
      return
    }

    loading.value = true
    errorMessage.value = null

    try {
      const updateData: Record<string, string | undefined> = {
        fullName: formValue.value.fullName.trim(),
        title: formValue.value.title.trim() || undefined,
      }

      const slugTrimmed = formValue.value.slug?.trim() || ''
      const initialSlug = originalSlug.value || ''
      if (slugTrimmed !== initialSlug) {
        updateData.slug = slugTrimmed
      }

      await updateProfile(updateData)
      completeMigration()
      emit('loginCallback', { eventType: LoginEvents.LOGIN_SUCCESSFUL })
    } catch (err: unknown) {
      errorMessage.value = getErrorMessage(err)
    } finally {
      loading.value = false
    }
  }
</script>

<template>
  <div class="flex w-full flex-col items-center">
    <div v-if="loadingUser" class="flex w-full justify-center py-8">
      <CPText size="md" class="text-secondary">
        {{ t('auth.profileConfirmation.loadingProfile') }}
      </CPText>
    </div>

    <template v-else>
      <AuthBrandingHeader :show-branding="showBranding" :portal-logo="portalLogo" :portal-name="portalName" />

      <CPForm
        :id="createElementId('profile-confirmation', 'form', 'main')"
        ref="formRef"
        class="flex w-full flex-col"
        :model="formValue"
        :rules="rules"
        @keyup.enter="handleConfirm"
      >
        <CPFormItem class="w-full" path="fullName" :label="t('auth.fullName')" required>
          <CPInput
            :id="createElementId('profile-confirmation', 'fullname', 'input')"
            v-model="formValue.fullName"
            size="sm"
            :maxlength="50"
            :placeholder="t('auth.fullNamePlaceholder')"
          >
            <template #prefix>
              <User01Icon class="mr-2 h-5 w-5 pr-1" />
            </template>
          </CPInput>
          <template #feedback>
            <CPText size="sm" :class="fieldErrors.fullName ? 'text-danger' : 'text-clientportal-font-secondary'">
              {{ fieldErrors.fullName || t('auth.profileConfirmation.fullNameHint') }}
            </CPText>
          </template>
        </CPFormItem>

        <CPFormItem class="w-full" path="slug" :label="t('auth.profileConfirmation.profileSlug')">
          <CPInput
            :id="createElementId('profile-confirmation', 'slug', 'input')"
            v-model="formValue.slug"
            size="sm"
            :maxlength="SLUG_MAX_LENGTH"
            :placeholder="t('auth.profileConfirmation.profileSlugPlaceholder')"
          />
          <template #feedback>
            <CPText size="sm" :class="fieldErrors.slug ? 'text-danger' : 'text-clientportal-font-secondary'">
              {{ fieldErrors.slug || t('auth.profileConfirmation.slugHint') }}
            </CPText>
          </template>
        </CPFormItem>

        <CPFormItem class="w-full" path="title" :label="t('auth.profileConfirmation.titleLabel')">
          <CPInput
            :id="createElementId('profile-confirmation', 'title', 'input')"
            v-model="formValue.title"
            size="sm"
            :maxlength="100"
            :placeholder="t('auth.profileConfirmation.titlePlaceholder')"
          />
          <template #feedback>
            <CPText size="sm" :class="fieldErrors.title ? 'text-danger' : 'text-clientportal-font-secondary'">
              {{ fieldErrors.title }}
            </CPText>
          </template>
        </CPFormItem>

        <div v-if="errorMessage" class="w-full py-2">
          <CPText size="sm" class="text-danger">
            {{ errorMessage }}
          </CPText>
        </div>

        <CPButton
          :id="createElementId('profile-confirmation', 'confirm', 'button')"
          class="w-full rounded-lg"
          type="primary"
          size="md"
          :disabled="loading || !isFormValid"
          :loading="loading"
          @click="handleConfirm"
        >
          {{ t('auth.profileConfirmation.confirmButton') }}
        </CPButton>
      </CPForm>
    </template>
  </div>
</template>
